'use client';
import Link from 'next/link';
import { useResource } from '@/lib/use-resource';

type HealthState = 'ok' | 'warn' | 'fail' | 'unknown';
interface SubIndicator { label: string; state: HealthState; value: string }
interface HealthBlock {
  state: HealthState;
  reason: string;
  checkedAt: string;
  detail?: string;
  sub?: SubIndicator;
}
interface HealthResponse {
  sapi: HealthBlock;
  terminal: HealthBlock;
  fmp: HealthBlock;
}

const DOT: Record<HealthState, string> = {
  ok:      'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]',
  warn:    'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.9)] animate-pulse',
  fail:    'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.9)] animate-pulse',
  unknown: 'bg-zinc-500/70 ring-1 ring-zinc-400/60',
};

function Pill({ name, h }: { name: string; h: HealthBlock | undefined }) {
  const state: HealthState = h?.state ?? 'unknown';
  const reason = h?.reason ?? 'checking…';
  const sub = h?.sub;
  const title = [
    `${name}: ${state.toUpperCase()}`,
    reason,
    sub ? `${sub.label}: ${sub.value}` : null,
    h?.detail ? `(${h.detail})` : null,
  ].filter(Boolean).join('\n');
  return (
    <Link
      href="/pipeline"
      title={title}
      className="flex items-center gap-1.5 px-2 py-0.5 rounded border border-[#1e293b] hover:border-[#334155] transition-colors"
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${DOT[state]} flex-shrink-0`} aria-hidden />
      <span className="text-[10px] font-semibold tracking-wider uppercase text-[#cbd5e1]">{name}</span>
    </Link>
  );
}

export function HealthPills() {
  const { data } = useResource<HealthResponse>('/api/health-check', 20_000);
  return (
    <div className="flex items-center gap-1.5">
      <Pill name="SAPI"     h={data?.sapi} />
      <Pill name="Term"     h={data?.terminal} />
      <Pill name="FMP"      h={data?.fmp} />
    </div>
  );
}
